import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { queryString, request } from './http';

const EVENT_COLORS = [
	'#4355AB',
	'#FF6B6B',
	'#4ECDC4',
	'#FFE66D',
	'#95E1D3',
	'#F38181',
	'#AA96DA',
	'#FCBAD3',
	'#A8D8EA',
	'#FFAAA7',
];

export interface HomeEvent {
	id: number;
	day: number;
	month: string;
	title: string;
	description: string;
	date: Date;
	color: string;
}

interface EventResponse {
	id: number;
	title: string;
	description: string;
	date: string;
}

/** `YYYY-MM` for the month that contains `date`, in local time. */
export const monthKey = (date: Date): string => {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	return `${date.getFullYear()}-${month}`;
};

/** Inclusive first and last calendar days of a `YYYY-MM` key. */
export const boundsForMonth = (key: string): { from: string; to: string } => {
	const [yearText, monthText] = key.split('-');
	const last = new Date(Number(yearText), Number(monthText), 0).getDate();
	return {
		from: `${yearText}-${monthText}-01`,
		to: `${yearText}-${monthText}-${String(last).padStart(2, '0')}`,
	};
};

/** A `YYYY-MM-DD` value is a calendar day, not a UTC instant. */
const parseCalendarDay = (value: string): Date => {
	const [year, month, day] = value.split('-').map(Number);
	return new Date(year, (month ?? 1) - 1, day ?? 1);
};

export const formatCalendarDay = (date: Date): string => {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
};

const formatMonth = (date: Date): string => {
	const formatted = dayjs(date).locale('es').format('MMM').replace('.', '');
	return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

export const createEvent = (body: {
	title: string;
	description: string;
	date: string;
}): Promise<EventResponse> =>
	request<EventResponse>('/api/events', {
		method: 'POST',
		body: JSON.stringify(body),
	});

/** A day inside `YYYY-MM`: today when that month is current, otherwise the 1st. */
export const defaultDateForMonth = (month: string): string => {
	const today = new Date();
	if (monthKey(today) !== month) return `${month}-01`;
	return `${month}-${String(today.getDate()).padStart(2, '0')}`;
};

export const updateEvent = (
	id: number,
	body: { title: string; description: string; date: string }
): Promise<EventResponse> =>
	request<EventResponse>(`/api/events/${id}`, {
		method: 'PUT',
		body: JSON.stringify(body),
	});

export const deleteEvent = (id: number): Promise<void> =>
	request<void>(`/api/events/${id}`, { method: 'DELETE' });

export const listEvents = async (month: string): Promise<HomeEvent[]> => {
	const rows = await request<EventResponse[]>(
		`/api/events${queryString(boundsForMonth(month))}`
	);
	return rows.map((row) => {
		const date = parseCalendarDay(row.date);
		return {
			id: row.id,
			day: date.getDate(),
			month: formatMonth(date),
			title: row.title,
			description: row.description,
			date,
			color: EVENT_COLORS[row.id % EVENT_COLORS.length],
		};
	});
};
